import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { UsersIcon, SearchIcon, BadgeDollarSignIcon, TargetIcon, EyeIcon, ArrowRightIcon, ShieldCheckIcon, LightbulbIcon, HeartIcon, FileSearchIcon, HandshakeIcon, TrendingUpIcon, StethoscopeIcon, ScaleIcon, LandmarkIcon, GraduationCapIcon, BuildingIcon, HotelIcon, QuoteIcon } from 'lucide-react';
const differentiators = [{
  icon: UsersIcon,
  title: 'Experts',
  subtitle: 'IN TELECOM COMMUNICATIONS',
  description: 'We represent and connect you to the most comprehensive tech companies in Business Communications.',
  bg: 'bg-slate-100'
}, {
  icon: SearchIcon,
  title: 'Exclusive',
  subtitle: 'RESEARCH FOR YOUR BUSINESS',
  description: "We test each provider's flexibility to work with your business needs. We guarantee that we can match you with the perfect source for your communications services.",
  bg: 'bg-blue-200/60'
}, {
  icon: BadgeDollarSignIcon,
  title: 'Cost Effective',
  subtitle: 'TO SAVE ON YOUR BUDGET',
  description: 'We respect your budget as we design the most economic options for your business needs. We will recommend and adjust services in order to meet your budget.',
  bg: 'bg-blue-300/50'
}];
const values = [{
  icon: ShieldCheckIcon,
  title: 'Integrity',
  description: 'We believe in honest partnerships and transparent recommendations that always put your business first.'
}, {
  icon: LightbulbIcon,
  title: 'Innovation',
  description: 'Staying ahead of telecom trends to provide you with the most modern and efficient solutions available.'
}, {
  icon: HeartIcon,
  title: 'Customer-First',
  description: 'Your success is our success. We provide dedicated support and personalized attention to every client.'
}, {
  icon: SearchIcon,
  title: 'Transparency',
  description: 'No hidden fees or complex jargon. We provide clear billing analysis and straightforward advice.'
}];
const history = [{
  year: '2005',
  title: 'The Founding',
  description: 'Ambient Telecom was established with a vision to simplify telecom procurement for small businesses.'
}, {
  year: '2012',
  title: 'Strategic Expansion',
  description: 'Expanded our service portfolio to include cloud infrastructure and advanced network security.'
}, {
  year: '2018',
  title: 'Market Leadership',
  description: 'Recognized as a leading partner for major telecom providers, serving over 300 active clients.'
}, {
  year: 'Present',
  title: 'Digital Transformation',
  description: 'Continuing to innovate with AI-driven bill auditing and comprehensive managed IT services.'
}];
const auditSteps = [{
  icon: FileSearchIcon,
  title: '1. Collect & Review',
  description: 'We gather your current telecom bills and contracts to establish a baseline of your current spend.'
}, {
  icon: SearchIcon,
  title: '2. Analyze & Identify',
  description: 'Our experts identify billing errors, unused services, and opportunities for better rates.'
}, {
  icon: HandshakeIcon,
  title: '3. Negotiate & Save',
  description: 'We negotiate directly with providers to secure the best possible pricing and implement savings.'
}];
const leadership = [{
  name: 'James Wilson',
  title: 'Founder & CEO',
  bio: 'With over 25 years in telecommunications, James founded Ambient Telecom to bring enterprise-level consulting to businesses of all sizes.',
  image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=400&h=400'
}, {
  name: 'Elena Rodriguez',
  title: 'VP of Operations',
  bio: 'Elena oversees our service delivery and vendor relationships, ensuring every client receives white-glove support and optimal pricing.',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=400&h=400'
}, {
  name: 'Marcus Johnson',
  title: 'Head of Technology',
  bio: 'Marcus leads our technical strategy, evaluating new UCaaS and cybersecurity solutions to keep our clients ahead of the curve.',
  image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=400&h=400'
}];
const industries = [{
  icon: StethoscopeIcon,
  title: 'Healthcare'
}, {
  icon: ScaleIcon,
  title: 'Legal'
}, {
  icon: LandmarkIcon,
  title: 'Finance'
}, {
  icon: GraduationCapIcon,
  title: 'Education'
}, {
  icon: BuildingIcon,
  title: 'Government'
}, {
  icon: HotelIcon,
  title: 'Hospitality'
}];
const clientTestimonials = [{
  quote: "Ambient Telecom's audit process uncovered thousands of dollars in billing errors from our previous provider. Their ongoing support is invaluable.",
  author: 'Robert M.',
  role: 'CFO, Manufacturing Corp'
}, {
  quote: "They didn't just sell us a phone system; they partnered with us to understand our workflow and recommended a UCaaS solution that transformed how we work.",
  author: 'Amanda K.',
  role: 'Director of IT, Healthcare Network'
}, {
  quote: 'The transparency and integrity they bring to the table is rare in this industry. We trust Ambient with all our connectivity needs.',
  author: 'Thomas L.',
  role: 'Operations VP, Logistics Inc'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function AboutPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-558">
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-559">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-560"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-561"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px] bg-primary/15 rounded-full blur-[100px] pointer-events-none" data-id="element-562"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-563">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-564">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-565">
              Your Partner in
              <br className="hidden md:block" data-id="element-566" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-567">
                Telecom Savings
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-568">
              Your trusted partner in telecom expense reduction and optimization
              since 2005.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 lg:py-28 bg-white" data-id="element-569">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-570">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-571">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-572">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" data-id="element-573">
                Who We Are
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed" data-id="element-574">
                <p data-id="element-575">
                  We are a full-service telecommunications and cloud services
                  procurement company that helps businesses save money on
                  Internet access, phone services, VoIP, wireless, and utility
                  services.
                </p>
                <p data-id="element-576">
                  With almost twenty years of experience, we've transformed from
                  a small consultancy into a market leader in Voice, Data,
                  Hosted VoIP, and Managed Cloud Services.
                </p>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-6" data-id="element-577">
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" data-id="element-578">
                  <div className="text-3xl font-bold text-primary mb-1" data-id="element-579">
                    20+
                  </div>
                  <div className="text-sm text-slate-500 font-medium uppercase tracking-wider" data-id="element-580">
                    Years Experience
                  </div>
                </div>
                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" data-id="element-581">
                  <div className="text-3xl font-bold text-primary mb-1" data-id="element-582">
                    300+
                  </div>
                  <div className="text-sm text-slate-500 font-medium uppercase tracking-wider" data-id="element-583">
                    Active Clients
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-584">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800" alt="Our Team" className="w-full h-full object-cover" data-id="element-585" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Leadership */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-586">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-587">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-588">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-589">
              Our Leadership Team
            </h2>
            <p className="text-lg text-slate-400" data-id="element-590">
              Guided by decades of industry experience, our team is dedicated to
              your success.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-591">
            {leadership.map((leader, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden" data-id="element-592">
                <img src={leader.image} alt={leader.name} className="w-full h-64 object-cover" data-id="element-593" />
                <div className="p-8" data-id="element-594">
                  <h3 className="text-xl font-bold mb-1" data-id="element-595">{leader.name}</h3>
                  <div className="text-accent font-medium mb-4" data-id="element-596">
                    {leader.title}
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed" data-id="element-597">
                    {leader.bio}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-slate-50" data-id="element-598">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-599">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-600">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-601">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600" data-id="element-602">
              The principles that guide every decision we make and every
              partnership we build.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-603">
            {values.map((value, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm text-center" data-id="element-604">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-605">
                  <value.icon className="w-6 h-6 text-primary" data-id="element-606" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-607">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-608">
                  {value.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-24 bg-white" data-id="element-609">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-610">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-611">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-612">
              Industries We Serve
            </h2>
            <p className="text-lg text-slate-600" data-id="element-613">
              We provide tailored telecom and IT solutions for specialized
              sectors.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-614">
            {industries.map((industry, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="bg-slate-50 border border-slate-100 p-6 rounded-2xl text-center hover:shadow-md transition-shadow" data-id="element-615">
                <industry.icon className="w-10 h-10 text-primary mx-auto mb-4" data-id="element-616" />
                <h3 className="font-bold text-slate-900 text-sm" data-id="element-617">
                  {industry.title}
                </h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* The Audit Process */}
      <section className="py-24 bg-slate-50" data-id="element-618">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-619">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-620">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-621">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-622">
                Our Bill Auditing Process
              </h2>
              <p className="text-lg text-slate-600 mb-10" data-id="element-623">
                We've refined our process over two decades to ensure we find
                every possible saving for your business.
              </p>
              <div className="space-y-8" data-id="element-624">
                {auditSteps.map((step, i) => <div key={i} className="flex gap-6" data-id="element-625">
                    <div className="shrink-0 w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center text-white" data-id="element-626">
                      <step.icon className="w-7 h-7" data-id="element-627" />
                    </div>
                    <div data-id="element-628">
                      <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-629">
                        {step.title}
                      </h3>
                      <p className="text-slate-600" data-id="element-630">{step.description}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="bg-slate-900 rounded-3xl p-10 text-white relative overflow-hidden" data-id="element-631">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl" data-id="element-632"></div>
              <h3 className="text-2xl font-bold mb-6 relative z-10" data-id="element-633">
                Why Audit?
              </h3>
              <ul className="space-y-4 relative z-10" data-id="element-634">
                {['80% of telecom bills contain errors', 'Average savings of 20-30% identified', 'No risk - we only get paid if we save you money', 'Continuous monitoring for future savings'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-635">
                    <ShieldCheckIcon className="w-5 h-5 text-primary" data-id="element-636" />
                    <span className="text-slate-300" data-id="element-637">{item}</span>
                  </li>)}
              </ul>
              <button onClick={() => onNavigate('contact')} className="mt-10 w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-xl transition-colors" data-id="element-638">
                Start Your Free Audit
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Our History Timeline */}
      <section className="py-24 bg-slate-50" data-id="element-639">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-640">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-641">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-642">
              Our Journey
            </h2>
            <p className="text-lg text-slate-600" data-id="element-643">
              Nearly two decades of helping businesses optimize their
              communications.
            </p>
          </div>
          <div className="relative" data-id="element-644">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-slate-200 hidden lg:block" data-id="element-645"></div>

            <div className="space-y-12" data-id="element-646">
              {history.map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className={`flex flex-col lg:flex-row items-center gap-8 ${i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`} data-id="element-647">
                  <div className="flex-1 text-center lg:text-right" data-id="element-648">
                    {i % 2 === 0 ? <div data-id="element-649">
                        <div className="text-3xl font-black text-primary mb-2" data-id="element-650">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-651">
                          {item.title}
                        </h3>
                        <p className="text-slate-600" data-id="element-652">{item.description}</p>
                      </div> : null}
                  </div>
                  <div className="w-4 h-4 bg-primary rounded-full relative z-10 hidden lg:block" data-id="element-653">
                    <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-25" data-id="element-654"></div>
                  </div>
                  <div className="flex-1 text-center lg:text-left" data-id="element-655">
                    {i % 2 !== 0 ? <div data-id="element-656">
                        <div className="text-3xl font-black text-primary mb-2" data-id="element-657">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-658">
                          {item.title}
                        </h3>
                        <p className="text-slate-600" data-id="element-659">{item.description}</p>
                      </div> : null}
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-24 bg-white" data-id="element-660">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-661">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-662">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-663">
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-600" data-id="element-664">
              Hear from businesses that have transformed their communications
              with us.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-665">
            {clientTestimonials.map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-slate-50 p-8 rounded-3xl border border-slate-100 relative" data-id="element-666">
                <QuoteIcon className="w-10 h-10 text-primary/20 absolute top-6 left-6" data-id="element-667" />
                <div className="relative z-10" data-id="element-668">
                  <p className="text-slate-700 italic mb-8 pt-4" data-id="element-669">
                    "{testimonial.quote}"
                  </p>
                  <div data-id="element-670">
                    <div className="font-bold text-slate-900" data-id="element-671">
                      {testimonial.author}
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-672">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-673">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-674">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-675"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-676"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-677">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-678">
            Ready to see how much you can save?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-679">
            Let our team of telecom experts audit your current bills and show
            you exactly where you're overspending.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-680">
            <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-681">
              Our Savings Process
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-682" />
            </button>
            <a href="tel:+4435451747" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-683">
              Call Us: (443) 545-1747
            </a>
          </div>
        </div>
      </section>
    </div>;
}